// Shared by every "this function threads a plain Set<string> of English
// warning text" spot in the codebase (engine readers via engineWarnings.ts,
// model/inp writers directly) — changing those Set<string> types to
// something structured would ripple through dozens of files and tests for a
// purely-display concern. Instead, `registerText()` is a drop-in replacement
// for building the English string at each call site — it returns the EXACT
// SAME string as before (so `Set<string>`'s own type, and every existing
// test asserting on that literal English text, is untouched), while ALSO
// registering that string's own translation key + params in a side table. A
// UI layer looks a message up here to render it in the user's own language,
// falling back to the raw English text for anything never routed through
// `registerText` (defensive, not expected once a family of warnings is
// fully migrated).
//
// The registry is module-level and never cleared: a given (key, params)
// combination always maps to the same English text (it's a pure function of
// its own inputs), so re-registering the same text on every call is a
// harmless overwrite with an identical value, not a leak of unbounded
// growth.
import type { TextKey } from "./en.ts";

export interface TextTranslation {
  key: TextKey;
  params?: Record<string, string | number>;
}

const registry = new Map<string, TextTranslation>();

/**
 * Builds (and registers the translation for) one warning/message's English
 * text. Every en.ts/es.ts pair for `key` must carry the same `{param}`-style
 * placeholders `template` already has, so `t(key, params)` renders the same
 * sentence, translated.
 */
export function registerText(
  key: TextKey,
  template: string,
  params?: Record<string, string | number>
): string {
  registry.set(template, { key, params });
  return template;
}

/** `undefined` for text never built via `registerText` — callers fall back to the raw English text in that case. */
export function getTextTranslation(message: string): TextTranslation | undefined {
  return registry.get(message);
}
